import * as THREE from 'three';
import { basic, cloth, emis, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import { kit } from './era4-kit.ts';
import { bear } from './heroes.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Kaela of the Moss (docs/ANTIHEROES.md): the druid of the old garrison who called the forest, and the forest came
 * late. The titan dreams her hooded in a robe gone to rags and moss, with black glass grown over her shoulders,
 * forearms and shins like bark, cracked through with a sick green light; a crown of black antlers through the hood,
 * two green eyes in its dark; in her right hand a long staff of glass-wood whose head is a crook of thorns round a
 * glowing seed, in her left a knot of thorned vines that drips light. A mantle of smoke hangs from her shoulders.
 *
 * On Ser Corvin's bones (so the fallen sentinel's walk, reel, stagger and fall fit her): body, chest, head, the
 * robe's front and back flaps, armL/foreL/shield (the knot of vines), armR/foreR/sword (the staff), legs.
 * Faces +Z, `R` limbs on +X.
 *
 * Her bear (`kaelaBear`, from 33 %): the druid's bear, but of the same black glass and green-lit moss.
 */

/** Model height (the antlers' tips). */
export const KAELA_HEIGHT = 2.35;

export const kaela = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 811);
  const { g, rng } = c;
  const k4 = kit(rng);

  const glass = metal('#272d2a', { roughness: 0.18, metalness: 0.5, bumpScale: 0.01 });
  const glassD = metal('#181c1a', { roughness: 0.3, metalness: 0.5, bumpScale: 0.02 });
  const robe = cloth('#1d241c', { side: THREE.DoubleSide });
  const robeD = cloth('#131812', { side: THREE.DoubleSide });
  const moss = std('#33502a', { roughness: 1, bumpScale: 0.06 });
  const wood = std('#1e1915', { roughness: 0.85, bumpScale: 0.06 });
  const thorn = metal('#202420', { roughness: 0.3, metalness: 0.4 });
  const sap = emis('#dcffc0', '#6cc832', 2.6);
  const sapF = emis('#dcffc0', '#8adf4a', 2.0, { transparent: true, opacity: 0.85, depthWrite: false });
  const smokeM = basic('#18201a', 0.55);

  const bone = (parent: THREE.Object3D, at: THREE.Vector3, name: string): { p: THREE.Group; abs: THREE.Group } => joint(parent, at, name);

  /* ---------- legs: glass greaves like bark over wrapped shins, bare-root feet ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = bone(g, V(k * 0.14, 1.0, 0), `leg${s}`);
    const hip = legB.abs;
    limb(hip, V(k * 0.14, 1.02, 0), V(k * 0.15, 0.58, 0.02), 0.115, 0.09, robeD, 7);
    const shinB = bone(hip, V(k * 0.15, 0.56, 0.02), `shin${s}`);
    const lo = shinB.abs;
    limb(lo, V(k * 0.15, 0.56, 0.02), V(k * 0.15, 0.12, 0.0), 0.095, 0.072, glass, 7);
    k4.crack(lo, [V(k * 0.15, 0.5, 0.09), V(k * 0.17, 0.36, 0.08), V(k * 0.14, 0.22, 0.075)], 0.007, sap, 0.03);
    // Feet that went to root: a glass sole, roots splaying out of it.
    const sole = M(new THREE.BoxGeometry(0.15, 0.08, 0.24), glassD, lo);
    sole.position.set(k * 0.15, 0.04, 0.05);
    for (let i = 0; i < 3; i++) {
      const a = -0.6 + i * 0.6;
      limb(lo, V(k * 0.15, 0.05, 0.12), V(k * 0.15 + Math.sin(a) * 0.14, 0.0, 0.12 + Math.cos(a) * 0.14), 0.022, 0.006, wood, 4);
    }
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips: the robe's skirt, a belt of cord and seed pods, its ragged front and back ---------- */
  const bodyB = bone(g, V(0, 1.02, 0), 'body');
  const body = bodyB.abs;
  M(new THREE.CylinderGeometry(0.24, 0.36, 0.42, 9, 1, true), robe, body).position.set(0, 0.84, 0);
  const belt = M(new THREE.TorusGeometry(0.25, 0.022, 3, 12), wood, body);
  belt.rotation.x = Math.PI / 2;
  belt.position.set(0, 1.1, 0);
  for (const [a, y] of [[0.6, 1.04], [1.3, 1.02], [-0.9, 1.03]] as const) {
    const pod = M(new THREE.SphereGeometry(0.035, 6, 4), sap, body);
    pod.position.set(Math.sin(a) * 0.26, y, Math.cos(a) * 0.26);
    pod.scale.set(1, 1.5, 1);
  }
  const flapF = bone(body, V(0, 1.08, 0.2), 'flapF');
  k4.panel(flapF.abs, V(0, 1.09, 0.23), 0.36, 0.95, 0.03, 1, robe, 0.6);
  k4.panel(flapF.abs, V(0.06, 1.0, 0.245), 0.16, 0.7, 0.02, 1, moss, 0.7);
  const flapB = bone(body, V(0, 1.08, -0.2), 'flapB');
  k4.panel(flapB.abs, V(0, 1.09, -0.23), 0.42, 1.0, 0.03, -1, robe, 0.55);

  /* ---------- chest: a robe over glass grown like bark, a green crack down the heart; moss on the shoulders ---------- */
  const chestB = bone(body, V(0, 1.16, 0), 'chest');
  const chest = chestB.abs;
  k4.swell(chest, robe, 0, 1.38, 0.0, 0.27, 0.3, 0.2, 9, 6);
  k4.swell(chest, glass, -0.05, 1.46, 0.03, 0.22, 0.2, 0.18, 8, 5);
  k4.crack(chest, [V(-0.14, 1.58, 0.17), V(-0.07, 1.5, 0.2), V(0.0, 1.42, 0.205), V(-0.03, 1.32, 0.19), V(0.05, 1.22, 0.17)], 0.012, sap, 0.05);
  k4.crack(chest, [V(0.0, 1.42, 0.205), V(0.09, 1.36, 0.19), V(0.15, 1.3, 0.16)], 0.008, sap, 0.04);
  glowSprite(chest, '#8adf4a', 0.4, V(-0.02, 1.42, 0.25), 0.55);
  for (const k of [-1, 1]) {
    const pd = G(chest, k * 0.3, 1.6, 0);
    pd.rotation.z = -k * 0.4;
    const dome = M(new THREE.SphereGeometry(1, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), glassD, pd);
    dome.scale.set(0.17, 0.13, 0.17);
    // Moss heaped on it, and two thorns of glass standing out.
    const tuft = M(new THREE.SphereGeometry(1, 7, 4, 0, Math.PI * 2, 0, Math.PI / 2), moss, pd);
    tuft.scale.set(0.15, 0.08, 0.14);
    tuft.position.y = 0.08;
    for (const [x, z, r] of [[0.03, 0.02, 0.5], [-0.05, -0.04, -0.3]] as const) {
      const sp = M(new THREE.ConeGeometry(0.025, 0.18, 4), thorn, pd);
      sp.position.set(k * x, 0.14, z);
      sp.rotation.z = -k * r;
    }
  }

  /* ---------- the mantle of smoke, frayed into wisps, moss along its hem ---------- */
  const mantle = G(chest, 0, 1.64, -0.18);
  for (const [x, w, h, rz] of [[-0.16, 0.28, 1.25, 0.07], [0.16, 0.28, 1.2, -0.07], [0, 0.32, 1.38, 0]] as const) {
    const pn = k4.panel(mantle, V(x, 0, -0.02), w, h, 0.02, -1, smokeM, 0.6);
    pn.rotation.set(0.12, 0, rz);
  }

  /* ---------- head: a deep hood, dark inside, two green eyes; a crown of black antlers through it ---------- */
  const headB = bone(chest, V(0, 1.68, 0.01), 'head');
  const head = headB.abs;
  const hood = M(new THREE.SphereGeometry(0.17, 9, 7, 0, Math.PI * 2, 0, Math.PI * 0.62), robeD, head);
  hood.position.set(0, 1.84, -0.01);
  hood.scale.set(1, 1.18, 1.1);
  const cowl = M(new THREE.ConeGeometry(0.2, 0.22, 9, 1, true), robeD, head);
  cowl.position.set(0, 1.72, -0.01);
  cowl.rotation.x = Math.PI;
  const dark = M(new THREE.SphereGeometry(0.12, 8, 6), std('#050705', { roughness: 1 }), head);
  dark.position.set(0, 1.82, 0.06);
  dark.scale.set(1, 1.1, 0.7);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.022, 6, 4), sap, head).position.set(k * 0.045, 1.83, 0.135);
    glowSprite(head, '#8adf4a', 0.22, V(k * 0.045, 1.83, 0.17), 0.95);
  }
  // A rim of moss round the hood's mouth.
  const rim = M(new THREE.TorusGeometry(0.135, 0.018, 4, 12), robeD, head);
  rim.position.set(0, 1.83, 0.09);
  rim.scale.set(1, 1.25, 0.6);
  for (const [x, y] of [[-0.1, 1.74], [0.09, 1.92], [0.11, 1.76]] as const) {
    const tf = M(new THREE.SphereGeometry(0.035, 5, 4), moss, head);
    tf.position.set(x, y, 0.11);
  }
  // The antlers: black glass, branching, their tips lit green.
  for (const k of [-1, 1]) {
    const base = V(k * 0.09, 1.96, -0.02);
    const mid = V(k * 0.2, 2.12, -0.06);
    const top = V(k * 0.26, 2.33, -0.02);
    limb(head, base, mid, 0.022, 0.016, glass, 5);
    limb(head, mid, top, 0.016, 0.008, glass, 5);
    for (const [a, b] of [[mid, V(k * 0.32, 2.16, 0.02)], [V(k * 0.23, 2.22, -0.04), V(k * 0.17, 2.32, 0.02)], [mid, V(k * 0.16, 2.22, -0.14)]] as const) {
      limb(head, a, b, 0.012, 0.006, glass, 4);
      M(new THREE.SphereGeometry(0.012, 4, 3), sap, head).position.copy(b);
    }
    M(new THREE.SphereGeometry(0.014, 4, 3), sap, head).position.copy(top);
    // Moss hanging from the antler.
    limb(head, mid, mid.clone().add(V(0, -0.16, 0.02)), 0.01, 0.004, moss, 3);
  }
  glowSprite(head, '#8adf4a', 0.5, V(0, 2.2, -0.03), 0.4);

  /* ---------- the left hand: a knot of thorned vines round a seed of light, dripping ---------- */
  const armLB = bone(chest, V(-0.3, 1.56, 0), 'armL');
  const aL = armLB.abs;
  limb(aL, V(-0.31, 1.56, 0), V(-0.36, 1.24, 0.05), 0.075, 0.065, robe, 6);
  const foreLB = bone(aL, V(-0.36, 1.23, 0.06), 'foreL');
  const fL = foreLB.abs;
  limb(fL, V(-0.36, 1.23, 0.06), V(-0.27, 1.12, 0.28), 0.066, 0.052, glass, 6);
  k4.crack(fL, [V(-0.35, 1.2, 0.1), V(-0.31, 1.16, 0.18), V(-0.28, 1.13, 0.25)], 0.006, sap, 0.02);
  const shieldB = bone(fL, V(-0.26, 1.11, 0.31), 'shield');
  const sh = shieldB.abs;
  {
    const knot = G(sh, -0.26, 1.08, 0.36);
    M(new THREE.SphereGeometry(0.06, 8, 6), sap, knot);
    for (let i = 0; i < 4; i++) {
      const ring = M(new THREE.TorusGeometry(0.1 + i * 0.012, 0.012, 4, 10), wood, knot);
      ring.rotation.set(i * 0.8, i * 1.3, i * 0.4);
      for (let j = 0; j < 3; j++) {
        const a = j * 2.1 + i;
        const tn = M(new THREE.ConeGeometry(0.012, 0.06, 3), thorn, ring);
        tn.position.set(Math.cos(a) * (0.1 + i * 0.012), Math.sin(a) * (0.1 + i * 0.012), 0);
        tn.rotation.z = a - Math.PI / 2;
      }
    }
    // Vines trailing down from it.
    for (const [x, z, h] of [[0.03, 0.02, 0.32], [-0.04, -0.02, 0.24]] as const) limb(knot, V(x, -0.06, z), V(x * 2, -h, z), 0.012, 0.004, wood, 4);
    glowSprite(knot, '#8adf4a', 0.45, V(0, 0, 0), 0.7);
  }

  /* ---------- the staff: a long glass-wood stave, its head a crook of thorns round a glowing seed ---------- */
  const armRB = bone(chest, V(0.3, 1.56, 0), 'armR');
  const aR = armRB.abs;
  limb(aR, V(0.31, 1.56, 0), V(0.37, 1.23, 0.02), 0.075, 0.065, robe, 6);
  const foreRB = bone(aR, V(0.37, 1.22, 0.02), 'foreR');
  const fR = foreRB.abs;
  limb(fR, V(0.37, 1.22, 0.02), V(0.4, 0.96, 0.12), 0.066, 0.054, glass, 6);
  k4.crack(fR, [V(0.38, 1.18, 0.08), V(0.39, 1.06, 0.12), V(0.4, 0.98, 0.15)], 0.006, sap, 0.02);
  const swordB = bone(fR, V(0.4, 0.92, 0.13), 'sword');
  const sw = swordB.abs;
  M(new THREE.SphereGeometry(0.055, 6, 5), glassD, sw).position.set(0.4, 0.92, 0.13);
  {
    // Held upright, a little forward.
    const st = G(sw, 0.4, 0.92, 0.13);
    st.rotation.x = 0.12;
    limb(st, V(0, -0.85, 0), V(0.01, 0.2, 0), 0.026, 0.03, wood, 6);
    limb(st, V(0.01, 0.2, 0), V(-0.01, 1.05, 0), 0.03, 0.024, glass, 6);
    k4.crack(st, [V(0.02, -0.4, 0.02), V(0.03, 0.1, 0.025), V(0.01, 0.6, 0.025), V(0.02, 0.95, 0.02)], 0.006, sap, 0.02);
    // The crook: thorned glass branches curling round a seed of green light.
    const hd = G(st, 0, 1.12, 0);
    const seed = M(new THREE.OctahedronGeometry(0.075, 0), sapF, hd);
    seed.scale.set(1, 1.4, 1);
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      const p0 = V(0, -0.1, 0);
      const p1 = V(Math.cos(a) * 0.12, 0.02, Math.sin(a) * 0.12);
      const p2 = V(Math.cos(a + 0.6) * 0.08, 0.2, Math.sin(a + 0.6) * 0.08);
      limb(hd, p0, p1, 0.016, 0.012, glass, 4);
      limb(hd, p1, p2, 0.012, 0.005, glass, 4);
      const tn = M(new THREE.ConeGeometry(0.012, 0.07, 3), thorn, hd);
      tn.position.copy(p1);
      tn.rotation.set(Math.sin(a) * 1.2, 0, -Math.cos(a) * 1.2);
    }
    limb(hd, V(0.08, -0.04, 0), V(0.1, -0.3, 0.02), 0.012, 0.004, moss, 3);
    glowSprite(hd, '#8adf4a', 0.75, V(0, 0, 0), 0.75);
  }

  /* ---------- smoke breathing off her, and spores ---------- */
  const wisps: Ribbon[] = [];
  for (const [x, z, h, y] of [[0.28, -0.2, 0.7, 1.5], [-0.26, -0.22, 0.65, 1.45], [0.04, -0.3, 0.85, 1.25], [-0.1, -0.28, 0.6, 0.85]] as const) {
    wisps.push(new Ribbon(chest, wisp(x, z, h, 4).map((p) => p.add(V(0, y, 0))), 0.05, smokeM, rng, 0.06));
  }

  const t = (_n: number): void => {
    for (const r of wisps) r.step();
  };
  t(0);
  return done(c, KAELA_HEIGHT, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, flapF: flapF.p, flapB: flapB.p,
    armL: armLB.p, foreL: foreLB.p, shield: shieldB.p, armR: armRB.p, foreR: foreRB.p, sword: swordB.p,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};

/** The druid's bear in her glass: black glass for fur, the moss and the stones burning green (her form from 33 %). */
export const kaelaBear = (o?: BaseOpts): Model => {
  const m = bear({ ...(o ?? {}), tier: 4 });
  const glass = metal('#232826', { roughness: 0.2, metalness: 0.5, bumpScale: 0.015 });
  const glassD = metal('#151917', { roughness: 0.3, metalness: 0.5, bumpScale: 0.02 });
  const mossLit = emis('#3e6a2c', '#6cc832', 0.9, { roughness: 1 });
  const sap = emis('#dcffc0', '#8adf4a', 1.8);
  const swap: Record<string, THREE.Material> = {
    '3d2b1f': glass, '2a1e16': glassD, '6a5240': glassD, '5d7a3a': mossLit, '3f5a2e': mossLit, a8977a: glassD,
  };
  m.root.traverse((x) => {
    const mesh = x as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mat = mesh.material as THREE.MeshStandardMaterial;
    if (!mat?.color || (mat.emissive && mat.emissive.getHex() !== 0)) return;
    const to = swap[mat.color.getHexString()];
    if (to) mesh.material = to;
  });
  // Green cracks along the flanks and the hump (on the body bone, so they move with it).
  // The body pivot's first child is its frame in model coordinates (see `pivot` in heroes.ts).
  const body = m.skel?.bones[m.skel.names.indexOf('body')]?.children[0];
  if (body) {
    const k4 = kit(mulberry32(97));
    for (const k of [-1, 1]) {
      k4.crack(body, [V(k * 0.4, 1.3, 0.5), V(k * 0.48, 1.15, 0.2), V(k * 0.46, 1.0, -0.1), V(k * 0.42, 1.1, -0.4)], 0.012, sap, 0);
    }
    glowSprite(body, '#8adf4a', 0.9, V(0, 1.5, 0.3), 0.35);
  }
  return m;
};
